/**
 * useTimetableEntryScreen.js
 * --------------------------
 * State hook for Education > Lectures > Time Table Entry screen.
 * Connects to live FastAPI endpoints, supports both /entry/:id and /entry?id=,
 * ensures edit pre-population never misses options, and handles clash validations.
 */
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import {
  getTimetableById,
  createTimetableEntry,
  updateTimetableEntry,
  fetchTimetableMasterOptions,
  DAYS_OPTIONS,
  TIMETABLE_SEARCH_FIELDS,
} from '../api/timetableApi'
import { appToast } from '../../../../components/AppToast'

const DAY_MAP_REVERSE = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
}

const DAY_MAP_FORWARD = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
}

function parseTimeParts(raw) {
  if (!raw) return { hour: '09', minute: '00', period: 'AM' }
  const str = String(raw).trim()
  const m = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i)
  if (m) {
    let h = parseInt(m[1], 10)
    const min = m[2]
    let ampm = m[3] ? m[3].toUpperCase() : null
    if (!ampm) {
      if (h >= 12) {
        ampm = 'PM'
        if (h > 12) h -= 12
      } else {
        ampm = 'AM'
        if (h === 0) h = 12
      }
    } else {
      if (h === 0) h = 12
    }
    return {
      hour: String(h).padStart(2, '0'),
      minute: min,
      period: ampm || 'AM',
    }
  }
  return { hour: '09', minute: '00', period: 'AM' }
}

function mergeOptionList(baseItems = [], extraItems = []) {
  const map = new Map()
  for (const item of extraItems) {
    if (item && item.value) {
      map.set(String(item.value), item)
    }
  }
  for (const item of baseItems) {
    if (item && item.value && !map.has(String(item.value))) {
      map.set(String(item.value), item)
    }
  }
  return Array.from(map.values())
}

const DEFAULT_FORM = {
  day: 'Monday',
  ignoreDuplication: false,
  planTime: { hour: '09', minute: '00', period: 'AM' },
  tillTime: { hour: '11', minute: '00', period: 'AM' },
  company: '',
  branch: '',
  branchType: 'Single',
  course: '',
  batch: '',
  batchType: 'Single',
  school: 'All',
  schoolType: 'All',
  subject: '',
  faculty: '',
  classroom: '',
}

export default function useTimetableEntryScreen() {
  const { id: paramId } = useParams()
  const [searchParams] = useSearchParams()
  const id = paramId || searchParams.get('id')
  const navigate = useNavigate()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(DEFAULT_FORM)

  // Real Dynamic Master Options from Backend (Rule 90)
  const [masterOptions, setMasterOptions] = useState({
    companies: [],
    branches: [],
    courses: [],
    batches: [],
    subjects: [],
    faculties: [],
    classrooms: [],
    schools: [],
  })

  const [extraOptions, setExtraOptions] = useState({
    companies: [],
    branches: [],
    courses: [],
    batches: [],
    subjects: [],
    faculties: [],
    classrooms: [],
    schools: [],
  })

  // Load dynamic options on mount
  useEffect(() => {
    async function loadOptions() {
      try {
        const res = await fetchTimetableMasterOptions()
        setMasterOptions({
          companies: res.companies,
          branches: res.branches,
          courses: res.courses,
          batches: res.batches,
          subjects: res.subjects,
          faculties: res.faculties,
          classrooms: res.classrooms,
          schools: [{ value: 'All', label: 'All Schools / Colleges' }, ...res.schools],
        })
      } catch (err) {
        console.error('Failed to load master options for timetable entry:', err)
      }
    }
    loadOptions()
  }, [])

  // Load existing record if editing
  useEffect(() => {
    async function loadRecord() {
      if (!id) {
        setForm(DEFAULT_FORM)
        return
      }
      try {
        setLoading(true)
        const existing = await getTimetableById(id)
        if (existing) {
          const pTime = parseTimeParts(existing.p_from || existing.planTime)
          const tTime = parseTimeParts(existing.p_till || existing.tillTime)

          const cVal = existing.course_id ? String(existing.course_id) : (existing.course_name || existing.course || '')
          const sVal = existing.subject_id ? String(existing.subject_id) : (existing.subject_name || existing.subject || '')
          const fVal = existing.faculty_id ? String(existing.faculty_id) : (existing.faculty_name || existing.faculty || '')
          const compVal = existing.institute_id ? String(existing.institute_id) : (existing.institute_name || existing.company || '')
          const roomVal = existing.classroom_id ? String(existing.classroom_id) : (existing.classroom_name || existing.classroom || '')
          const bVal = existing.branches_csv || existing.branch_names || existing.branch || ''
          const batchVal = existing.batches_csv || existing.batch_names || existing.batch || ''
          const schoolVal = existing.school_id ? String(existing.school_id) : (existing.school_name || existing.school || 'All')

          setExtraOptions({
            courses: existing.course_name && cVal ? [{ value: cVal, label: existing.course_name }] : [],
            subjects: existing.subject_name && sVal ? [{ value: sVal, label: existing.subject_name }] : [],
            branches: existing.branch_names && bVal ? [{ value: bVal, label: existing.branch_names }] : [],
            batches: existing.batch_names && batchVal ? [{ value: batchVal, label: existing.batch_names }] : [],
            faculties: existing.faculty_name && fVal ? [{ value: fVal, label: existing.faculty_name }] : [],
            classrooms: existing.classroom_name && roomVal ? [{ value: roomVal, label: existing.classroom_name }] : [],
            companies: existing.institute_name && compVal ? [{ value: compVal, label: existing.institute_name }] : [],
            schools: existing.school_name && schoolVal && schoolVal !== 'All' ? [{ value: schoolVal, label: existing.school_name }] : [],
          })

          const dayStr =
            existing.day_name ||
            DAY_MAP_REVERSE[existing.day_of_week] ||
            existing.dayOfWeek ||
            'Monday'

          setForm({
            day: dayStr,
            ignoreDuplication: Boolean(existing.ignore_duplication || existing.ignoreDuplication),
            planTime: pTime,
            tillTime: tTime,
            company: compVal,
            branch: bVal,
            branchType: existing.branch_type === 2 || existing.branchType === 'All' ? 'All' : 'Single',
            course: cVal,
            batch: batchVal,
            batchType: existing.batch_type === 2 || existing.batchType === 'All' ? 'All' : 'Single',
            school: schoolVal,
            schoolType: existing.school_type === 2 || existing.schoolType === 'All' ? 'All' : 'Single',
            subject: sVal,
            faculty: fVal,
            classroom: roomVal,
          })
        } else {
          appToast.error('Timetable entry not found.')
          navigate('/lectures/timetable')
        }
      } catch (err) {
        console.error('Failed to load timetable record:', err)
        appToast.error('Failed to load timetable record.')
        navigate('/lectures/timetable')
      } finally {
        setLoading(false)
      }
    }
    loadRecord()
  }, [id, navigate])

  const setField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }, [])

  const mergedOptions = useMemo(() => {
    return {
      days: DAYS_OPTIONS,
      searchFields: TIMETABLE_SEARCH_FIELDS,
      companies: mergeOptionList(masterOptions.companies, extraOptions.companies),
      branches: mergeOptionList(masterOptions.branches, extraOptions.branches),
      courses: mergeOptionList(masterOptions.courses, extraOptions.courses),
      batches: mergeOptionList(masterOptions.batches, extraOptions.batches),
      subjects: mergeOptionList(masterOptions.subjects, extraOptions.subjects),
      schools: mergeOptionList(masterOptions.schools, extraOptions.schools),
      faculties: mergeOptionList(masterOptions.faculties, extraOptions.faculties),
      classrooms: mergeOptionList(masterOptions.classrooms, extraOptions.classrooms),
    }
  }, [masterOptions, extraOptions])

  const handleSave = useCallback(
    async (isContinue = false) => {
      if (!form.day) {
        appToast.error('Please select Day of the week.')
        return
      }
      if (!form.course) {
        appToast.error('Please select Course.')
        return
      }
      if (!form.subject) {
        appToast.error('Please select Subject.')
        return
      }
      if (!form.faculty) {
        appToast.error('Please select Faculty.')
        return
      }

      setSaving(true)
      try {
        const dowNum = DAY_MAP_FORWARD[form.day] || 1
        const pFromStr = `${form.planTime.hour}:${form.planTime.minute} ${form.planTime.period}`
        const pTillStr = `${form.tillTime.hour}:${form.tillTime.minute} ${form.tillTime.period}`

        const findId = (val, list = []) => {
          if (!val) return null
          const num = Number(val)
          if (!isNaN(num) && num > 0) return num
          const found = list.find((item) => String(item.value) === String(val) || item.label === val)
          if (found) {
            const n = Number(found.value)
            return !isNaN(n) && n > 0 ? n : null
          }
          return null
        }

        const courseId = findId(form.course, mergedOptions.courses) || 1
        const subjectId = findId(form.subject, mergedOptions.subjects) || 1
        const facultyId = findId(form.faculty, mergedOptions.faculties) || 1
        const instituteId = findId(form.company, mergedOptions.companies) || 1
        const classroomId = findId(form.classroom, mergedOptions.classrooms) || null
        const schoolId = form.school === 'All' ? null : (findId(form.school, mergedOptions.schools) || null)

        const payload = {
          day_of_week: dowNum,
          institute_id: instituteId,
          branch_type: form.branchType === 'All' ? 2 : 1,
          branches_csv: form.branch || '',
          course_id: courseId,
          batch_type: form.batchType === 'All' ? 2 : 1,
          batches_csv: form.batch || '',
          school_type: form.schoolType === 'All' ? 2 : 1,
          school_id: schoolId,
          subject_id: subjectId,
          faculty_id: facultyId,
          classroom_id: classroomId,
          p_from: pFromStr,
          p_till: pTillStr,
        }

        if (isEditMode) {
          await updateTimetableEntry(id, payload)
          appToast.success('Timetable updated successfully!')
        } else {
          await createTimetableEntry(payload)
          appToast.success('Timetable saved successfully!')
        }

        if (isContinue) {
          // Advance slot by 1 hour
          let currentTillHour = parseInt(form.tillTime.hour, 10) || 10
          let nextTillHour = currentTillHour + 1
          if (nextTillHour > 12) nextTillHour = 1
          const period = form.tillTime.period || 'AM'

          setForm((prev) => ({
            ...prev,
            planTime: { ...prev.tillTime },
            tillTime: {
              hour: String(nextTillHour).padStart(2, '0'),
              minute: prev.tillTime.minute,
              period,
            },
            subject: '',
          }))
        } else {
          navigate('/lectures/timetable')
        }
      } catch (err) {
        console.error('Failed to save timetable entry:', err)
        const errMsg = err?.response?.data?.detail || err?.message || 'Failed to save timetable entry.'
        appToast.error(errMsg)
      } finally {
        setSaving(false)
      }
    },
    [form, id, isEditMode, navigate],
  )

  return {
    state: {
      form,
      isEditMode,
      loading,
      saving,
      options: mergedOptions,
    },
    actions: {
      setField,
      handleSave,
    },
  }
}
